import { TOAST_DURATION_MS } from "@scayla/components";
import { defineStore } from "pinia";
import { ref } from "vue";

export interface AsyncOperation {
  id: string;
  /** Stable across rekey() — the render key, so a v-for keyed on it doesn't remount the card. */
  key: string;
  icon: string;
  title: string;
  subtitle?: string;
  percentage: number;
  status: "running" | "success";
  /** Opaque tag, never read by this store — lets external code (e.g. an SSE feed) find its own operations. */
  kind?: string;
}

/**
 * Generic long-running-operation progress, rendered via ProgressToastCard from inside
 * GlobalProgressToast.vue (mounted once in BaseLayout so it survives navigation away from the
 * page that started the operation). Deliberately decoupled from any one feature's data —
 * fileUploadQueue.ts is the upload/AI-classification-specific equivalent; this one is for
 * everything else (tenant copy, and any future long-running action) that just needs a label, a
 * percentage and a done state.
 */
export const useAsyncOperationProgress = defineStore("asyncOperationProgress", () => {
  const operations = ref<AsyncOperation[]>([]);

  function start(operation: { id: string; icon: string; title: string; subtitle?: string; kind?: string }) {
    operations.value = [
      ...operations.value.filter((o) => o.id !== operation.id),
      { ...operation, key: operation.id, percentage: 0, status: "running" },
    ];
  }

  function update(id: string, patch: Partial<Pick<AsyncOperation, "percentage" | "title" | "subtitle">>) {
    const operation = operations.value.find((o) => o.id === id);
    if (operation) Object.assign(operation, patch);
  }

  function rekey(oldId: string, newId: string) {
    const operation = operations.value.find((o) => o.id === oldId);
    if (operation) operation.id = newId;
  }

  function finish(id: string) {
    const operation = operations.value.find((o) => o.id === id);
    if (operation) {
      operation.percentage = 100;
      operation.status = "success";
    }
    setTimeout(() => remove(id), TOAST_DURATION_MS);
  }

  function remove(id: string) {
    operations.value = operations.value.filter((o) => o.id !== id);
  }

  return { operations, start, update, rekey, finish, remove };
});
